/**
 * Utilidades para persistencia de datos en JSON
 * En un entorno de producción, esto se conectaría a una API backend
 */

const STORAGE_KEYS = {
  CITAS: 'citas',
  LECTURAS: 'lecturas',
  EVENTOS: 'eventos',
};

/**
 * Guarda datos en localStorage
 * @param {string} key - Clave de almacenamiento
 * @param {Array} data - Datos a guardar
 */
function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error(`Error guardando ${key}:`, error);
  }
}

/**
 * Carga datos desde localStorage
 * @param {string} key - Clave de almacenamiento
 * @returns {Array} Datos cargados
 */
function loadFromStorage(key) {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error(`Error cargando ${key}:`, error);
    return [];
  }
}

// ============= Citas Ordenadas =============

/**
 * Guarda todas las citas
 * @param {Array} citas - Array de citas
 */
export function saveCitas(citas) {
  saveToStorage(STORAGE_KEYS.CITAS, citas);
}

/**
 * Carga todas las citas
 * @returns {Array} Array de citas
 */
export function loadCitas() {
  return loadFromStorage(STORAGE_KEYS.CITAS);
}

/**
 * Añade una nueva cita
 * @param {Object} cita - Cita a añadir
 */
export function addCita(cita) {
  const citas = loadCitas();
  citas.push(cita);
  saveCitas(citas);
}

/**
 * Actualiza una cita existente
 * @param {string} id - ID de la cita
 * @param {Object} updatedCita - Datos actualizados
 */
export function updateCita(id, updatedCita) {
  const citas = loadCitas();
  const index = citas.findIndex(c => c.id === id);
  if (index !== -1) {
    citas[index] = { ...citas[index], ...updatedCita };
    saveCitas(citas);
  }
}

/**
 * Elimina una cita
 * @param {string} id - ID de la cita
 */
export function deleteCita(id) {
  const citas = loadCitas();
  const filtered = citas.filter(c => c.id !== id);
  saveCitas(filtered);
}

// ============= Lecturas =============

/**
 * Guarda todas las lecturas
 * @param {Array} lecturas - Array de lecturas
 */
export function saveLecturas(lecturas) {
  saveToStorage(STORAGE_KEYS.LECTURAS, lecturas);
}

/**
 * Carga todas las lecturas
 * @returns {Array} Array de lecturas
 */
export function loadLecturas() {
  return loadFromStorage(STORAGE_KEYS.LECTURAS);
}

/**
 * Añade una nueva lectura
 * @param {Object} lectura - Lectura a añadir
 */
export function addLectura(lectura) {
  const lecturas = loadLecturas();
  lecturas.push(lectura);
  saveLecturas(lecturas);
}

/**
 * Actualiza una lectura existente
 * @param {string} id - ID de la lectura
 * @param {Object} updatedLectura - Datos actualizados
 */
export function updateLectura(id, updatedLectura) {
  const lecturas = loadLecturas();
  const index = lecturas.findIndex(l => l.id === id);
  if (index !== -1) {
    lecturas[index] = { ...lecturas[index], ...updatedLectura };
    saveLecturas(lecturas);
  }
}

/**
 * Elimina una lectura
 * @param {string} id - ID de la lectura
 */
export function deleteLectura(id) {
  const lecturas = loadLecturas();
  const filtered = lecturas.filter(l => l.id !== id);
  saveLecturas(filtered);
}

// ============= Eventos =============

/**
 * Guarda todos los eventos
 * @param {Array} eventos - Array de eventos
 */
export function saveEventos(eventos) {
  saveToStorage(STORAGE_KEYS.EVENTOS, eventos);
}

/**
 * Carga todos los eventos
 * @returns {Array} Array de eventos
 */
export function loadEventos() {
  return loadFromStorage(STORAGE_KEYS.EVENTOS);
}

/**
 * Añade un nuevo evento
 * @param {Object} evento - Evento a añadir
 */
export function addEvento(evento) {
  const eventos = loadEventos();
  eventos.push(evento);
  saveEventos(eventos);
}

/**
 * Actualiza un evento existente
 * @param {string} id - ID del evento
 * @param {Object} updatedEvento - Datos actualizados
 */
export function updateEvento(id, updatedEvento) {
  const eventos = loadEventos();
  const index = eventos.findIndex(e => e.id === id);
  if (index !== -1) {
    eventos[index] = { ...eventos[index], ...updatedEvento };
    saveEventos(eventos);
  }
}

/**
 * Elimina un evento
 * @param {string} id - ID del evento
 */
export function deleteEvento(id) {
  const eventos = loadEventos();
  const filtered = eventos.filter(e => e.id !== id);
  saveEventos(filtered);
}

// ============= Importar/Exportar =============

/**
 * Exporta todos los datos a JSON
 * @returns {string} String JSON con todos los datos
 */
export function exportAllData() {
  const data = {
    citas: loadCitas(),
    lecturas: loadLecturas(),
    eventos: loadEventos(),
    exportDate: new Date().toISOString(),
  };
  
  return JSON.stringify(data, null, 2);
}

/**
 * Importa datos desde JSON
 * @param {string} jsonString - String JSON con datos
 * @returns {boolean} True si la importación fue exitosa
 */
export function importData(jsonString) {
  try {
    const data = JSON.parse(jsonString);
    
    if (data.citas) {
      saveCitas(data.citas);
    }
    
    if (data.lecturas) {
      saveLecturas(data.lecturas);
    }
    
    if (data.eventos) {
      saveEventos(data.eventos);
    }
    
    return true;
  } catch (error) {
    console.error('Error importando datos:', error);
    return false;
  }
}

/**
 * Limpia todos los datos
 */
export function clearAllData() {
  localStorage.removeItem(STORAGE_KEYS.CITAS);
  localStorage.removeItem(STORAGE_KEYS.LECTURAS);
  localStorage.removeItem(STORAGE_KEYS.EVENTOS);
}















